import React, { type ComponentProps } from 'react';
import { defineMessages, FormattedMessage } from 'react-intl';
import { StyleSheet, type TextStyle, View, type ViewStyle } from 'react-native';
import { Button, DisplayText, theme, useResponsive, VStack } from '@sg/garnish';

import { Loyalty } from '@order/features/loyalty';
import { useA11ySelector } from '@order/utils';

import { useLoyaltyContent } from '../../../../hooks';

export const LoyaltyHomeFaq = (props: LoyaltyHomeFaqProps) => {
  const {
    palette,
    style,
    showHowItWorksModal,
    showHowItWorksButton = true,
    onForgotToScan,
  } = props;

  const a11ySelector = useA11ySelector('loyalty.faq.container');
  const { match } = useResponsive();
  const { faqLoyaltyHome } = useLoyaltyContent({ availablePoints: undefined });

  if (faqLoyaltyHome.length === 0) return null;

  return (
    <View
      style={[
        match([styles.containerXS, styles.containerSM]),
        containerPallettes[palette],
        style,
      ]}
      {...a11ySelector.props}
    >
      <View style={styles.header}>
        <DisplayText style={palettes[palette]} sizeMatch={['32', '48']}>
          <FormattedMessage {...messages.title} />
        </DisplayText>

        {showHowItWorksButton ? (
          <Button
            size="small"
            palette={buttonPalettes[palette]}
            onPress={showHowItWorksModal}
          >
            <FormattedMessage {...messages.howItWorks} />
          </Button>
        ) : null}
      </View>

      <VStack hasDivider gap={0}>
        {faqLoyaltyHome.map((entry) => (
          <Loyalty.FAQEntry key={entry.title} entry={entry} palette={palette} />
        ))}
      </VStack>

      {onForgotToScan ? (
        <Button
          size={match(['large', 'large-wide'])}
          palette={buttonPalettes[palette]}
          onPress={onForgotToScan}
        >
          <FormattedMessage {...messages.forgotToScan} />
        </Button>
      ) : null}
    </View>
  );
};

// ─── Messages ───────────────────────────────────────────────────────────────

const messages = defineMessages({
  title: {
    defaultMessage: 'FAQ',
    description: 'Loyalty | Home | FAQ | Title',
  },
  howItWorks: {
    defaultMessage: 'How it works',
    description: 'Loyalty | Home | FAQ | How it works button',
  },
  forgotToScan: {
    defaultMessage: 'Forgot to scan?',
    description: 'Loyalty | Home | FAQ | Forgot to scan button',
  },
});

// ─── Styles ─────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  containerXS: {
    gap: theme.spacing['6'],
    padding: theme.spacing['6'],
  },
  containerSM: {
    gap: theme.spacing['10'],
    padding: theme.spacing['6'],
  },
  darkBackground: {
    borderBottomWidth: 1,
    borderColor: theme.colors.CREAM,
  },
  kaleText: {
    color: theme.colors.KALE,
  },
  creamText: {
    color: theme.colors.CREAM,
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
});

// ─── Palettes ───────────────────────────────────────────────────────────────

const containerPallettes: Record<
  LoyaltyHomeFaqProps['palette'],
  ViewStyle | undefined
> = {
  kale: undefined,
  cream: styles.darkBackground,
};

const palettes: Record<LoyaltyHomeFaqProps['palette'], TextStyle> = {
  kale: styles.kaleText,
  cream: styles.creamText,
};

const buttonPalettes: Record<
  LoyaltyHomeFaqProps['palette'],
  ComponentProps<typeof Button>['palette']
> = {
  kale: 'kale',
  cream: 'cream',
};

// ─── Types ──────────────────────────────────────────────────────────────────

type LoyaltyHomeFaqProps = {
  palette: 'kale' | 'cream';
  showHowItWorksModal?: () => void;
  showHowItWorksButton?: boolean;
  onForgotToScan?: () => void;
  style?: ViewStyle;
};
